/**
 * Gather data for the Lender Packet PDF.
 * When ?debug=true (or ?debug) is in the URL, uses debug/demo data (same trigger as Dashboard).
 * Otherwise uses real data from Firestore and the investor metrics API.
 */

import { getAuth } from "firebase/auth";
import { getDebugMode, getDebugUnitCount } from "../helpers/constants";
import {
  getDebugUnitsExtended,
  getDebugTenants,
  getDebugPayments,
  getDebugDashboardStats,
} from "../helpers/debugData";
import { getInvestorMetrics } from "./api/investorMetricsApi";
import unitModel from "./firebase/unitModel";
import { getUnitDisplayAddress } from "./unitAddressUtils";
import { listTenants } from "./firebase/tenantModel";
import { getPaymentsByOwner } from "./firebase/paymentModel";
import { getExpensesByOwner } from "./firebase/expenseModel";
import SettingsModel from "./firebase/settingsModel";

/**
 * Build trailing 12 months from a single summary (debug/fallback).
 * @param {{ monthlyGrossRent?: number, monthlyNetIncome?: number, yearToDate?: { expenses?: number, cashFlow?: number } }} summary
 * @returns {Array<{ month: string, grossRent: number, expenses: number, noi: number, cashFlow: number }>}
 */
function buildT12FromSummary(summary) {
  const income =
    summary?.monthlyGrossRent ?? summary?.yearToDate?.grossRent ?? 0;
  const noi = summary?.monthlyNetIncome ?? summary?.yearToDate?.netIncome ?? 0;
  const expenses = summary?.yearToDate?.expenses ?? Math.max(0, income - noi);
  const cashFlow = summary?.yearToDate?.cashFlow ?? 0;
  const months = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      month: d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
      grossRent: income,
      expenses,
      noi,
      cashFlow,
    });
  }
  return months;
}

/**
 * Build trailing 12 months from real payments and expenses (actual income/expenses per month).
 * @param {Array<{ dueDate?: any, status?: string, amount?: number }>} payments - All payments (we filter by status and dueDate)
 * @param {Array<{ date?: any, amount?: number }>} expenses - All expenses (we filter by date)
 * @param {number} [debtServiceMonthly] - Optional monthly debt service for cash flow
 * @returns {Array<{ month: string, grossRent: number, expenses: number, noi: number, cashFlow: number }>}
 */
function buildT12FromPaymentsAndExpenses(
  payments,
  expenses,
  debtServiceMonthly
) {
  const now = new Date();
  const monthlyIncome = {};
  const monthlyExpenses = {};
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    monthlyIncome[key] = 0;
    monthlyExpenses[key] = 0;
  }
  const keys = Object.keys(monthlyIncome).sort();
  payments.forEach((p) => {
    const status = (p.status || "").toLowerCase();
    if (status !== "paid" && status !== "succeeded") return;
    const due = p.dueDate?.toDate ? p.dueDate.toDate() : new Date(p.dueDate);
    if (Number.isNaN(due.getTime())) return;
    const key = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    if (monthlyIncome[key] !== undefined) {
      monthlyIncome[key] += Number(p.amount) || 0;
    }
  });
  expenses.forEach((e) => {
    const d = e.date?.toDate ? e.date.toDate() : new Date(e.date);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    if (monthlyExpenses[key] !== undefined) {
      monthlyExpenses[key] += Number(e.amount) || 0;
    }
  });
  const debtPerMonth =
    typeof debtServiceMonthly === "number" && debtServiceMonthly > 0
      ? debtServiceMonthly
      : 0;
  return keys.map((key) => {
    const [y, m] = key.split("-").map(Number);
    const labelDate = new Date(y, m - 1, 1);
    const grossRent = monthlyIncome[key] ?? 0;
    const exp = monthlyExpenses[key] ?? 0;
    const noi = grossRent - exp;
    const cashFlow = noi - debtPerMonth;
    return {
      month: labelDate.toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      }),
      grossRent,
      expenses: exp,
      noi,
      cashFlow,
    };
  });
}

// ---------------------------------------------------------------------------
// Debug / Demo path (triggered by ?debug=true or ?debug in URL)
// ---------------------------------------------------------------------------

/**
 * Build lender packet payload from debug data. Used when getDebugMode() is true.
 * @param {number} debugUnitCount
 * @returns {Promise<{ address: string, noi?: number, dscr?: number, occupancyRate?: number, t12: Array, rentRoll: Array }>}
 */
async function getLenderPacketDataDebug(debugUnitCount) {
  const units = getDebugUnitsExtended(debugUnitCount);
  const tenants = getDebugTenants(debugUnitCount);
  const stats = getDebugDashboardStats(debugUnitCount);

  const rentRoll = tenants.map((t) => {
    const unit = units.find((u) => u.id === t.unitId);
    return {
      propertyName: getUnitDisplayAddress(unit) || unit?.name || "-",
      unitNumber: unit?.unitNumber ?? t.unitNumber ?? "-",
      tenantName: t.name ?? "-",
      monthlyRent: t.rentAmount ?? t.monthlyRent ?? unit?.currentRent ?? 0,
      status: "current",
    };
  });

  const totalMonthlyRent =
    stats.totalMonthlyRent ?? rentRoll.reduce((s, r) => s + r.monthlyRent, 0);
  const expensesEstimate = Math.round(totalMonthlyRent * 0.35);
  const noiEstimate = totalMonthlyRent - expensesEstimate;
  const debtServiceDemo = Math.round(noiEstimate / 1.35);
  const dscrDemo = debtServiceDemo > 0 ? noiEstimate / debtServiceDemo : null;

  const t12 = buildT12FromSummary({
    monthlyGrossRent: totalMonthlyRent,
    monthlyNetIncome: noiEstimate,
    yearToDate: {
      expenses: expensesEstimate,
      cashFlow: noiEstimate - debtServiceDemo,
    },
  });

  const occupancyRate =
    stats.totalUnits > 0
      ? Math.round(
          (100 * (stats.occupiedUnits ?? rentRoll.length)) / stats.totalUnits
        )
      : 100;

  return {
    address: "Portfolio Summary (Demo)",
    noi: noiEstimate,
    dscr: dscrDemo,
    occupancyRate,
    t12,
    rentRoll,
  };
}

// ---------------------------------------------------------------------------
// Real data path (Firestore + investor metrics API)
// ---------------------------------------------------------------------------

/**
 * Build lender packet payload from real data. Used when debug mode is off.
 * @param {string} uid - Current user (owner) ID
 * @param {string} month - YYYY-MM
 * @returns {Promise<{ address: string, noi?: number, dscr?: number, occupancyRate?: number, t12: Array, rentRoll: Array }>}
 */
async function getLenderPacketDataReal(uid, month) {
  const [
    units,
    tenantsResult,
    paymentsResult,
    expensesResult,
    metrics,
    userSettings,
  ] = await Promise.all([
    unitModel.getByOwner(uid),
    listTenants(),
    getPaymentsByOwner({}),
    getExpensesByOwner({}),
    getInvestorMetrics(),
    SettingsModel.getOrCreateSettings().catch(() => ({})),
  ]);

  const tenants = tenantsResult?.tenants ?? [];
  const payments = paymentsResult?.success ? paymentsResult.payments ?? [] : [];
  const expenses = expensesResult?.expenses ?? [];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const rentRoll = [];
  for (const tenant of tenants) {
    const unit = units.find((u) => u.id === tenant.unitId);
    if (!unit) continue;
    const rent = Number(
      unit.currentRent ??
        unit.baseRent ??
        tenant.monthlyRent ??
        tenant.rentAmount ??
        0
    );
    rentRoll.push({
      propertyName: getUnitDisplayAddress(unit) || unit?.name || "-",
      unitNumber: unit.unitNumber ?? unit.name ?? "-",
      tenantName:
        tenant.name ??
        ([tenant.firstName, tenant.lastName].filter(Boolean).join(" ") || "-"),
      monthlyRent: rent,
      status: "current",
    });
  }

  const totalMonthlyRent = rentRoll.reduce(
    (s, r) => s + (r.monthlyRent || 0),
    0
  );
  const occupiedCount = rentRoll.length;
  const totalUnits = units.length;
  const occupancyRate =
    totalUnits > 0 ? Math.round((100 * occupiedCount) / totalUnits) : undefined;

  const paymentsThisMonth = payments.filter((p) => {
    const d = p.dueDate?.toDate?.() ?? p.dueDate;
    if (!d) return false;
    const date = new Date(d);
    return (
      date.getMonth() === currentMonth && date.getFullYear() === currentYear
    );
  });
  const collectedThisMonth = paymentsThisMonth
    .filter((p) => {
      const s = (p?.status || "").toLowerCase();
      return s === "paid" || s === "succeeded";
    })
    .reduce((s, p) => s + (Number(p.amount) || 0), 0);

  const startOfMonth = new Date(currentYear, currentMonth, 1);
  const endOfMonth = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59);
  const expensesThisMonth = expenses
    .filter((e) => {
      const d = e.date instanceof Date ? e.date : new Date(e.date);
      return d >= startOfMonth && d <= endOfMonth;
    })
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const noi =
    typeof metrics?.noi === "number"
      ? metrics.noi
      : collectedThisMonth > 0 || expensesThisMonth > 0
      ? collectedThisMonth - expensesThisMonth
      : totalMonthlyRent - expensesThisMonth;

  const debtServiceFromUnits = units.reduce(
    (sum, u) => sum + (Number(u.mortgage?.monthlyPayment) || 0),
    0
  );
  const settingsDebtService =
    typeof userSettings?.portfolio?.monthlyDebtService === "number"
      ? userSettings.portfolio.monthlyDebtService
      : undefined;
  const debtService =
    metrics?.debtService ??
    settingsDebtService ??
    (debtServiceFromUnits > 0 ? debtServiceFromUnits : undefined);
  const noiNum = typeof noi === "number" ? noi : undefined;
  const dscr =
    typeof metrics?.dscr === "number"
      ? metrics.dscr
      : noiNum != null && debtService > 0
      ? noiNum / debtService
      : undefined;

  // Real trailing 12 months: actual income and expenses per month from payments and expenses
  const t12 = buildT12FromPaymentsAndExpenses(
    payments,
    expenses,
    debtService ?? undefined
  );

  return {
    address: "Portfolio Summary",
    noi: noiNum,
    dscr,
    occupancyRate,
    t12,
    rentRoll,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fetch all data needed for the Lender Packet PDF.
 * Uses debug/demo data when ?debug=true (or ?debug) is in the URL; otherwise uses real data.
 * @param {string} [selectedMonth] - YYYY-MM for rent roll; defaults to current month
 * @param {string} [address] - Override address (e.g. single property address)
 * @param {string} [propertyPhotoUrl] - Optional property image URL
 * @returns {Promise<{ address: string, propertyPhotoUrl?: string, noi?: number, dscr?: number, occupancyRate?: number, t12: Array, rentRoll: Array }>}
 */
export async function getLenderPacketData(
  selectedMonth,
  address,
  propertyPhotoUrl
) {
  const month = selectedMonth || new Date().toISOString().slice(0, 7);

  let data;
  if (getDebugMode()) {
    data = await getLenderPacketDataDebug(getDebugUnitCount());
  } else {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");
    data = await getLenderPacketDataReal(user.uid, month);
  }

  return {
    ...data,
    address: address || data.address,
    propertyPhotoUrl: propertyPhotoUrl || undefined,
  };
}
